import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { API_ERROR_MESSAGES } from '@hsc/common/constants/api-error-messages';
import { API_ENDPOINTS } from '@hsc/common/constants/endpoints';
import { AxiomQuoteLambdaResponse } from '@hsc/common/schemas/axiom.schema';
import { USER_NOTIFICATIONS } from '@constants/user-notifications';
import { ApigeeService } from '@services/apigee.service';
import { FullStoryService } from '@services/fullstory.service';
import { YES_NO } from '@utilities/form-utilities';
import { ApigeeError } from '@utilities/http-utilities';

import { QuotingFlowStore } from '../../services/quoting-flow.store';

@Component({
  selector: 'hsc-quote-review-step',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './quote-review.step.html',
})
export class QuoteReviewStep implements OnInit {
  // Services
  readonly qfStore = inject(QuotingFlowStore);
  readonly apigeeService = inject(ApigeeService);
  private readonly fullStoryService = inject(FullStoryService);

  // Service Properties
  readonly apigeeIsLoading = this.apigeeService.isLoading;

  // Component Properties
  readonly axiomQuoteState = this.qfStore.axiomQuoteState;
  readonly hasEquipmentBreakdown = this.qfStore.propertyReviewForm.controls.hasEquipmentBreakdown.value === YES_NO.YES;

  ngOnInit(): void {
    // Skip if a quote was already generated (e.g. user navigated back then forward)
    if (this.axiomQuoteState().id) return;
    this.requestAxiomQuote();
    if (this.hasEquipmentBreakdown) {
      this.qfStore.setNotificationState(USER_NOTIFICATIONS.equipmentBreakdownDoubleCheck);
    }
  }

  // Methods
  private requestAxiomQuote(): void {
    this.apigeeService
      .post<AxiomQuoteLambdaResponse>(API_ENDPOINTS.axiomQuote.invoke(), this.qfStore.propertyReviewForm.getRawValue())
      .subscribe({
        next: (response) => this.handleAxiomQuoteSuccess(response),
        error: (error: ApigeeError) => this.handleAxiomQuoteError(error),
      });
  }

  private handleAxiomQuoteSuccess(response: AxiomQuoteLambdaResponse): void {
    this.qfStore.patchFromAxiomQuote(response);
    // Disable ability to edit property form after creating quote
    this.qfStore.propertyReviewForm.disable();
    this.fullStoryService.event('Axiom Quote Generated');
  }

  private handleAxiomQuoteError(error: ApigeeError): void {
    const notification =
      error.message === API_ERROR_MESSAGES.AXIOM_NOT_ELIGIBLE
        ? USER_NOTIFICATIONS.axiomNotEligible
        : USER_NOTIFICATIONS.systemError;
    this.qfStore.setNotificationState(notification);
    this.fullStoryService.event('Axiom Quote Failed');
  }

  private handleAxiomQuoteEmail(): void {
    const { id, emailToClient, emailSent } = this.axiomQuoteState();
    // Prevent sending email if no quote, box not checked, or already sent
    if (!id || !emailToClient || emailSent) return;

    this.apigeeService
      .post(API_ENDPOINTS.axiomEmail.invoke(id), {
        email: this.qfStore.propertyReviewForm.controls.customer.controls.emailAddress.value,
      })
      .subscribe({
        next: () => {
          this.axiomQuoteState.update((aqs) => ({ ...aqs, emailSent: true }));
          this.fullStoryService.event('Email Quote Sent');
        },
        error: () => this.qfStore.setNotificationState(USER_NOTIFICATIONS.systemError),
      });
  }

  toggleEmailToClient(): void {
    const emailToClient = !this.axiomQuoteState().emailToClient;
    this.axiomQuoteState.update((s) => ({ ...s, emailToClient }));
    if (emailToClient) this.fullStoryService.event('Email Quote Opted In');
  }

  handleForward(): void {
    this.handleAxiomQuoteEmail();
    this.qfStore.handleForward();
  }

  handleBackward(): void {
    this.qfStore.handleBackward();
  }
}
